// src/StudentContext.js
import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from './firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc
} from 'firebase/firestore';

export const StudentContext = createContext();

const cleanVietnameseId = (str) => {
  if (!str) return '';
  let slug = str.toString().toLowerCase();
  slug = slug.replace(/đ/g, 'd');
  slug = slug.replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a');
  slug = slug.replace(/[éèẻẽẹêếềểễệ]/g, 'e');
  slug = slug.replace(/[íìỉĩị]/g, 'i');
  slug = slug.replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o');
  slug = slug.replace(/[úùủũụưứừửữự]/g, 'u');
  slug = slug.replace(/[ýỳỷỹỵ]/g, 'y');
  slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  slug = slug.replace(/[^a-z0-9]/g, '-');
  slug = slug.replace(/-+/g, '-');
  slug = slug.replace(/^-+|-+$/g, '');
  return slug;
};

export const StudentProvider = ({ children }) => {
  const [currentYearId, setCurrentYearId] = useState('');
  const [studentsData, setStudentsData] = useState({});
  const [transcriptsData, setTranscriptsData] = useState({});
  const [catechistsData, setCatechistsData] = useState([]);
  const [attendanceDates, setAttendanceDates] = useState([]);
  const [catechismAttendanceDates, setCatechismAttendanceDates] = useState([]);
  const [isAssignmentActive, setIsAssignmentActive] = useState(true);

  // State lưu trạng thái khóa ngày điểm danh
  const [dateLocks, setDateLocks] = useState({});

  // 🌟 State lưu danh sách các lớp được phân công đi phụ của GLV hiện tại
  const [substituteClasses, setSubstituteClasses] = useState([]);

  const [classMap, setClassMap] = useState({});
  const [allBlocks, setAllBlocks] = useState({});
  const [visibleBlocks, setVisibleBlocks] = useState({});

  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [noClassAssigned, setNoClassAssigned] = useState(false);

  const rawDataRef = useRef({
    rawGrades: [],
    rawClasses: [],
    rawStudents: {},
    rawEnrollments: {},
    rawMySubstitute: null // 🌟 Lưu dữ liệu phụ lớp realtime
  });

  const rebuildContextData = useCallback(() => {
    const { rawGrades, rawClasses, rawStudents, rawEnrollments, rawMySubstitute } = rawDataRef.current;
    if (!rawGrades || rawGrades.length === 0 || !rawClasses || rawClasses.length === 0) return;

    const userRole = localStorage.getItem('userRole') || 'catechist';
    const catechistClassName = localStorage.getItem('catechistClassName') || '';
    const blockLeader = localStorage.getItem('blockLeader') || '';
    const departmentLeader = localStorage.getItem('departmentLeader') || '';

    const gradeOrderMap = {};
    const groupOrderMap = new Map();
    rawGrades.forEach(g => {
      gradeOrderMap[g.id] = g.sortOrder || 0;
      if (g.group) {
        const currentMinOrder = groupOrderMap.get(g.group) ?? 999;
        if ((g.sortOrder || 0) < currentMinOrder) {
          groupOrderMap.set(g.group, g.sortOrder || 0);
        }
      }
    });

    const newClassMap = {};
    rawClasses.forEach(c => {
      newClassMap[c.id] = c.name || c.id;
    });
    setClassMap(newClassMap);

    const newAllBlocks = {};
    const sortedClasses = [...rawClasses].sort((a, b) => {
      const orderA = gradeOrderMap[a.gradeId] ?? 999;
      const orderB = gradeOrderMap[b.gradeId] ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });

    sortedClasses.forEach(c => {
      const groupName = c.group || 'Khác';
      const groupCleanId = cleanVietnameseId(groupName) || 'khac';

      if (!newAllBlocks[groupCleanId]) {
        newAllBlocks[groupCleanId] = [];
      }
      newAllBlocks[groupCleanId].push({
        id: c.id,
        name: c.name || c.id,
        group: groupName
      });
    });

    setAllBlocks(newAllBlocks);

    const newStudentsData = {};
    const newTranscriptsData = {};

    Object.keys(newClassMap).forEach(cId => {
      newStudentsData[cId] = [];
      newTranscriptsData[cId] = [];
    });

    Object.keys(rawEnrollments).forEach(sId => {
      const enrollment = rawEnrollments[sId];
      const cId = enrollment.classId;
      const profile = rawStudents[sId] || {};

      if (cId && newStudentsData[cId]) {
        newStudentsData[cId].push({
          ...profile,
          id: sId,
          classId: cId,
          className: newClassMap[cId] || cId,
          status: profile.status || 'Còn đi học',
          // 🌟 BỔ SUNG DÒNG NÀY ĐỂ TRUYỀN BẢNG ĐIỂM SANG CLASSTRANSCRIPT 🌟
          transcript: enrollment.transcript || {}
        });

        if (enrollment.transcript) {
          newTranscriptsData[cId].push({
            id: sId,
            holyName: profile.holyName || '',
            fullName: profile.fullName || sId,
            className: newClassMap[cId] || cId,
            semester1: enrollment.transcript.semester1 || {},
            semester2: enrollment.transcript.semester2 || {},
            yearly: enrollment.transcript.yearly || {},
          });
        }
      }
    });

    setStudentsData(newStudentsData);
    setTranscriptsData(newTranscriptsData);

    // 🌟 TỔNG HỢP DANH SÁCH LỚP PHỤ ĐƯỢC PHÂN CÔNG
    const assignedSubList = Array.isArray(rawMySubstitute?.assignedClasses)
      ? rawMySubstitute.assignedClasses
      : [];
    const subClassIdsSet = new Set();
    const subClassNamesSet = new Set();

    assignedSubList.forEach((item) => {
      if (typeof item === 'string') {
        subClassIdsSet.add(item.trim().toLowerCase());
      } else if (item && typeof item === 'object') {
        if (item.classId) subClassIdsSet.add(String(item.classId).trim().toLowerCase());
        if (item.className) subClassNamesSet.add(String(item.className).trim().toLowerCase());
      }
    });
    setSubstituteClasses(assignedSubList);

    // 5. PHÂN QUYỀN KHỐI & LỚP HIỂN THỊ (BAO GỒM LỚP CHÍNH VÀ LỚP ĐI PHỤ)
    let finalVisibleBlocks = {};

    if (userRole === 'admin' || departmentLeader === 'Ban Hành Chánh' || departmentLeader === 'Ban Kỷ Luật') {
      finalVisibleBlocks = newAllBlocks;
      setNoClassAssigned(false);
    } else {
      const cleanUserBlockLeader = cleanVietnameseId(blockLeader);
      const cleanUserClassName = catechistClassName.trim().toLowerCase();

      Object.keys(newAllBlocks).forEach(groupCleanId => {
        const classesInGroup = newAllBlocks[groupCleanId];
        const groupName = classesInGroup[0]?.group || '';
        const cleanGroupName = cleanVietnameseId(groupName);

        const isLeaderOfThisGroup = blockLeader && (
          cleanUserBlockLeader === groupCleanId ||
          cleanUserBlockLeader === cleanGroupName ||
          groupName.toLowerCase().includes(blockLeader.toLowerCase()) ||
          blockLeader.toLowerCase().includes(groupName.toLowerCase())
        );

        if (isLeaderOfThisGroup) {
          finalVisibleBlocks[groupCleanId] = classesInGroup;
        } else {
          // Kiểm tra khớp lớp chính HOẶC lớp được phân công đi phụ
          const matchedClasses = classesInGroup
            .filter(c => {
              const cNameLower = (c.name || '').trim().toLowerCase();
              const cIdLower = (c.id || '').trim().toLowerCase();

              const isPrimary =
                cleanUserClassName &&
                (cNameLower === cleanUserClassName || cIdLower === cleanUserClassName);

              const isSubstitute =
                subClassIdsSet.has(cIdLower) ||
                subClassIdsSet.has(cNameLower) ||
                subClassNamesSet.has(cNameLower);

              return isPrimary || isSubstitute;
            })
            .map(c => {
              const cNameLower = (c.name || '').trim().toLowerCase();
              const cIdLower = (c.id || '').trim().toLowerCase();
              const isPrimary =
                cleanUserClassName &&
                (cNameLower === cleanUserClassName || cIdLower === cleanUserClassName);
              return {
                ...c,
                isSubstitute: !isPrimary // Đánh dấu nếu là lớp đi phụ
              };
            });

          if (matchedClasses.length > 0) {
            finalVisibleBlocks[groupCleanId] = matchedClasses;
          }
        }
      });

      if (Object.keys(finalVisibleBlocks).length === 0) {
        setNoClassAssigned(true);
      } else {
        setNoClassAssigned(false);
      }
    }

    setVisibleBlocks(finalVisibleBlocks);
    setIsDataLoaded(true);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    let unsubs = [];

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      unsubs.forEach(unsub => unsub && unsub());
      unsubs = [];

      if (!user) {
        setCurrentYearId('');
        setStudentsData({});
        setTranscriptsData({});
        setCatechistsData([]);
        setVisibleBlocks({});
        setSubstituteClasses([]);
        setAttendanceDates([]);
        setCatechismAttendanceDates([]);
        setDateLocks({});
        setIsDataLoaded(false);
        setNoClassAssigned(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
      const unsubYear = onSnapshot(yearQuery, (snapshot) => {
        let currentDoc = snapshot.docs[0];
        let targetYearId = '';

        if (currentDoc) {
          targetYearId = currentDoc.id;
          setIsAssignmentActive(currentDoc.data().isAssignmentActive !== false);
        }

        if (targetYearId) {
          setCurrentYearId(targetYearId);

          const unsubGrades = onSnapshot(collection(firestore, `academic_years/${targetYearId}/grades`), (snap) => {
            rawDataRef.current.rawGrades = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            rebuildContextData();
          });

          const unsubClasses = onSnapshot(collection(firestore, `academic_years/${targetYearId}/classes`), (snap) => {
            rawDataRef.current.rawClasses = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            rebuildContextData();
          });

          const unsubEnrollments = onSnapshot(collection(firestore, `academic_years/${targetYearId}/enrollments`), (snap) => {
            const map = {};
            snap.docs.forEach(d => {
              const data = d.data();
              map[data.studentId || d.id] = data;
            });
            rawDataRef.current.rawEnrollments = map;
            rebuildContextData();
          });

          // Lắng nghe Realtime trạng thái Khóa ngày điểm danh
          const unsubLocks = onSnapshot(doc(firestore, `academic_years/${targetYearId}/settings`, 'attendance_locks'), (snap) => {
            if (snap.exists()) {
              setDateLocks(snap.data() || {});
            } else {
              setDateLocks({});
            }
          });

          // 🌟 Lắng nghe Realtime phân công phụ lớp của GLV đang đăng nhập
          const unsubMySubstitute = onSnapshot(
            doc(firestore, `academic_years/${targetYearId}/substitute_assignments`, user.uid),
            (snap) => {
              if (snap.exists()) {
                rawDataRef.current.rawMySubstitute = snap.data();
              } else {
                rawDataRef.current.rawMySubstitute = null;
              }
              rebuildContextData();
            },
            (err) => {
              console.error('Lỗi lắng nghe phân công phụ lớp:', err);
            }
          );

          unsubs.push(unsubGrades, unsubClasses, unsubEnrollments, unsubLocks, unsubMySubstitute);
        } else {
          setIsLoading(false);
        }
      }, (error) => {
        console.error('Lỗi khi lắng nghe Niên khóa:', error);
        setIsLoading(false);
      });

      const unsubStudents = onSnapshot(collection(firestore, 'students'), (snap) => {
        const map = {};
        snap.docs.forEach(d => { map[d.id] = d.data(); });
        rawDataRef.current.rawStudents = map;
        rebuildContextData();
      });

      const unsubStaff = onSnapshot(collection(firestore, 'staff'), (snap) => {
        const list = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
        setCatechistsData(list);

        const currentUid = localStorage.getItem('uid') || user.uid;
        if (currentUid) {
          const myProfile = list.find(s => s.uid === currentUid);
          if (myProfile) {
            localStorage.setItem('userRole', myProfile.role || 'catechist');
            localStorage.setItem('catechistClassName', myProfile.className || '');
            localStorage.setItem('blockLeader', myProfile.blockLeader || '');
            localStorage.setItem('departmentLeader', myProfile.departmentLeader || '');
          }
        }
        rebuildContextData();
      });

      unsubs.push(unsubYear, unsubStudents, unsubStaff);
    });

    return () => {
      unsubscribeAuth();
      unsubs.forEach(unsub => unsub && unsub());
    };
  }, [rebuildContextData]);

  const refreshData = useCallback(() => {
    rebuildContextData();
  }, [rebuildContextData]);

  const updateLocalCatechist = (uid, updates) => {
    setCatechistsData((prev) =>
      prev.map((cat) => (cat.uid === uid ? { ...cat, ...updates } : cat))
    );
  };

  const updateAttendanceInContext = useCallback((studentId, type, dateKey, timeStatus, catechistName) => {
    setStudentsData((prevData) => {
      const updatedData = { ...prevData };
      Object.keys(updatedData).forEach((cId) => {
        updatedData[cId] = updatedData[cId].map((student) => {
          if (student.id === studentId) {
            const attKey = type === 'church' ? 'attendance' : 'catechismAttendance';
            return {
              ...student,
              [attKey]: {
                ...(student[attKey] || {}),
                [dateKey]: {
                  time: timeStatus,
                  catechist: catechistName
                }
              }
            };
          }
          return student;
        });
      });
      return updatedData;
    });
  }, []);

  return (
    <StudentContext.Provider
      value={{
        currentYearId,
        studentsData,
        transcriptsData,
        catechistsData,
        classMap,
        blocks: visibleBlocks,
        allBlocks,
        substituteClasses, // 🌟 Cung cấp danh sách lớp phụ cho toàn ứng dụng
        attendanceDates,
        catechismAttendanceDates,
        dateLocks,
        refreshData,
        loadAndFilterData: refreshData,
        loadAllData: refreshData,
        isLoading,
        isDataLoaded,
        noClassAssigned,
        updateLocalCatechist,
        isAssignmentActive,
        updateAttendanceInContext,
      }}
    >
      {children}
    </StudentContext.Provider>
  );
};